import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { BackrsUser } from '@backrs_ui/common-ui';

import { fetchUserViaAutoLogin } from './thunks/user.thunk';
import { checkUserDocuments } from './thunks/userDocument.thunk';
import { UserDocuments } from './user.lubav.api';
import { handlePending, handleRejected } from '../reducers';
import { satActBoosterApi } from '../satActBooster/satActBooster.api';
import { logout } from '../session/logoutThunk';
import { RootState } from '../store';
import { trackAssessmentApi } from '../trackAssessment/trackAssessment.api';
import { postAPISurveyResults } from '../userResponse/thunks/userResponse.thunk';

type UserState = Readonly<{
  user?: BackrsUser;
  documents: UserDocuments;
  skippedSignIn: boolean;
  isGuidanceCounselor: boolean;
  loading: boolean;
  documentsLoading: boolean;
  error?: string;
}>;

const initialState: UserState = {
  user: undefined,
  isGuidanceCounselor: false,
  skippedSignIn: false,
  loading: false,
  documents: {
    resume: [],
    transcript: [],
    personal_assessment: [],
    other: [],
  },
  documentsLoading: true,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<BackrsUser>) => {
      state.user = action.payload;
    },
    skipSignIn: (state) => {
      state.skippedSignIn = true;
    },
    setIsGuidanceCounselor: (state, action: PayloadAction<boolean>) => {
      state.isGuidanceCounselor = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      /**
       * AUTO LOGIN
       */
      .addCase(fetchUserViaAutoLogin.pending, handlePending)
      .addCase(fetchUserViaAutoLogin.rejected, handleRejected)
      .addCase(
        fetchUserViaAutoLogin.fulfilled,
        (state, action: PayloadAction<BackrsUser | null>) => {
          if (!action.payload) {
            state.loading = false;

            return;
          }

          state.user = action.payload;
          state.loading = false;
        },
      )

      /**
       * USER DOCUMENTS
       */
      .addCase(checkUserDocuments.pending, (state) => {
        state.documentsLoading = true;
      })
      .addCase(checkUserDocuments.rejected, (state) => {
        state.documentsLoading = false;
      })
      .addCase(checkUserDocuments.fulfilled, (state, action: PayloadAction<UserDocuments>) => {
        state.loading = false;
        if (state.user) {
          state.documents = action.payload;
        }

        state.documentsLoading = false;
      })

      /**
       * BLUEPRINT COMPLETED
       */
      .addCase(postAPISurveyResults.fulfilled, (state) => {
        if (state.user) {
          state.user = { ...state.user, has_blueprint: true };
        }
      })

      /**
       * LOGOUT
       */
      .addCase(logout, (state) => {
        state.user = undefined;
        state.loading = false;
        state.error = undefined;
      })

      /**
       * TRACK ASSESSMENT COMPLETED
       */
      .addMatcher(trackAssessmentApi.endpoints.assessMyTrack.matchFulfilled, (state) => {
        if (state.user) {
          state.user = { ...state.user, has_track_assessment: true };
        }
      })

      /**
       * TEST BOOSTER INTAKE ZIP
       */
      .addMatcher(satActBoosterApi.endpoints.createSubmission.matchFulfilled, (state, action) => {
        const { zip } = action.meta.arg.originalArgs.draft;

        // Saving a submission back-fills a blank zip on the account, and no query re-reads it.
        if (state.user && !state.user.zip_code && zip) {
          state.user = { ...state.user, zip_code: zip };
        }
      });
  },
});

export const { skipSignIn, setUser, setIsGuidanceCounselor } = userSlice.actions;

// Reducer
export const userReducer = userSlice.reducer;

// Selectors
export const isAuthenticated = (state: RootState) => !!state.user?.user;

export const selectUser = (state: RootState) => state.user?.user;

export const selectUserId = (state: RootState): number | undefined => {
  return state.user.user?.id;
};

export const selectDoesUserHaveAccessToken = (state: RootState): boolean => {
  if (state.user.user) {
    return !!state.user.user.access_token;
  }

  return false;
};

export const selectProfilePhoto = (state: RootState) => {
  return state.user.user?.profile_image;
};

export const selectUserDocuments = (state: RootState) => state.user.documents;
